import { Constants } from './Constants.js';
import { JournalOperations } from './JournalOperations.js';
import {
  AlbumsEntity,
  BulkImageObject,
  BulkImageRefObject,
  StandardPhotoObject,
  StandardKrakenObject,
  FileObject,
} from './media-adopter.interface.js';
import { BulkApiObject } from './media-adopter.interface.js';
import { MmbPhotosObject } from './media-adopter.interface.js';

export class MediaObjectMapper {
  static getStandardPhotoObject(
    source: string,
    photoObj: any,
    brand: string,
  ): StandardPhotoObject[] {
    let standardObjects: StandardPhotoObject[] = [];

    switch (source) {
      case 'kraken':
        switch (brand) {
          case Constants.BRAND_SFLY:
            photoObj.forEach((photoObj: any) => {
              let journalParams = [];
              const { imageID, DATA } = this.extractImageDataFromUrl(
                photoObj.smallThumbnailUrl,
              );
              if (
                photoObj.sourceCrop &&
                photoObj.sourceCrop.llx != null &&
                photoObj.sourceCrop.lly != null &&
                photoObj.sourceCrop.urx != null &&
                photoObj.sourceCrop.ury != null
              ) {
                let cropll: number[];
                let cropur: number[];

                if (photoObj.shouldNotConsiderCrop) {
                  cropll = [0, 0];
                  cropur = [1, 1];
                } else {
                  cropll = [
                    parseFloat(photoObj.sourceCrop.llx),
                    parseFloat(photoObj.sourceCrop.lly),
                  ];
                  cropur = [
                    parseFloat(photoObj.sourceCrop.urx),
                    parseFloat(photoObj.sourceCrop.ury),
                  ];
                }

                if (
                  photoObj.sourceRotation === 90 ||
                  photoObj.sourceRotation === 180 ||
                  photoObj.sourceRotation === 270
                ) {
                  const rotatedCrop = MediaObjectMapper.unrotateShutterflyCrop(
                    cropll,
                    cropur,
                    photoObj.sourceRotation,
                  );
                  cropll = rotatedCrop.cropll;
                  cropur = rotatedCrop.cropur;
                }

                journalParams.push(
                  'cropll=' +
                    cropll[0] +
                    ',' +
                    cropll[1] +
                    '&cropur=' +
                    cropur[0] +
                    ',' +
                    cropur[1],
                );
              }

              if (photoObj.sourceRotation && photoObj.sourceRotation > 0) {
                journalParams.push('rotation=' + photoObj.sourceRotation);
              }

              if (photoObj.sourceEffect !== '') {
                journalParams.push('effect=' + photoObj.sourceEffect);
              }

              if (photoObj.enhance && photoObj.enhance != null) {
                journalParams.push('enhance=' + photoObj.enhance);
              }

              if (
                photoObj.redeye !== undefined &&
                photoObj.redeye !== null &&
                photoObj.redeye !== ''
              ) {
                journalParams.push(`redeye=${photoObj.redeye}`);
              }

              const mappedCropping = MediaObjectMapper.getJournalMapping(
                journalParams,
                'snf',
              );

              let formattedString = '';

              if (Object.keys(mappedCropping).length > 0) {
                formattedString = '/';
                Object.keys(mappedCropping).forEach((key) => {
                  const value = mappedCropping[key];
                  formattedString += value + '/';
                });
              }

              const smallThumbnailUrl = `imageID=${imageID}&DATA=${DATA}`;

              let sflyUserID: number;
              if (
                photoObj.anon === true &&
                (!photoObj.sflyId || photoObj.sflyId === null)
              ) {
                sflyUserID = photoObj.locationSpec?.split('.')[0] || '';
              } else {
                sflyUserID = photoObj.sflyId;
              }

              let exifHeaders = photoObj.exifHeaders
                ? { ...photoObj.exifHeaders }
                : null;

              let finalWidth: number;
              let finalHeight: number;

              finalWidth = photoObj.width;
              finalHeight = photoObj.height;

              standardObjects.push({
                _id: String(sflyUserID),
                createDate: new Date(photoObj.uploadTime).getTime(),
                deletionDate: undefined,
                updateDate: undefined,
                version: undefined,
                markedForDelete: undefined,
                fileName: photoObj.filename,
                origin: photoObj.origin,
                dataCenter: 'AUS',
                context: '/sfly/shutterfly/shutterfly-us/default',
                assetSource: 'shutterfly',
                reportingMediaSource: 'shutterfly',
                ownerAccountId: undefined,
                userTags: [
                  { key: 'userFileName', value: photoObj.title },
                  { key: 'caption', value: photoObj.title },
                  { key: 'fileName', value: photoObj.locationSpec },
                  {
                    key: 'locationSpec',
                    value:
                      photoObj.locationSpec ||
                      photoObj.data.locationSpec ||
                      photoObj.data.locationspec ||
                      photoObj.data.locSpec ||
                      photoObj.data.locspec ||
                      '',
                  },
                ],
                systemTags: [
                  { key: 'journalDeveloper', value: '' },
                  { key: 'journalCore', value: formattedString },
                  { key: 'assetStatus', value: 'AVAILABLE' },
                ],
                status: 'AVAILABLE',
                currentImageInfo: {
                  currentWidth: photoObj.width,
                  currentHeight: photoObj.height,
                },
                exifTags: [],
                exifHeaders: exifHeaders,
                assetType: 'PICTURE',
                files: [
                  {
                    fileType: 'HIRES',
                    url: undefined,
                    width: finalWidth,
                    height: finalHeight,
                    size: undefined,
                  },
                  {
                    fileType: 'THUMBNAIL',
                    url: smallThumbnailUrl,
                    width: finalWidth,
                    height: finalHeight,
                    size: undefined,
                  },
                ],
                parentCollectionReferences: [
                  { ownerAccountId: undefined, collectionId: undefined },
                ],
                faces: [],
                dateTaken: photoObj.captureTime,
                subType: 'JPG',
                id: String(photoObj.sflyId),
              });
            });

            break;
          case Constants.BRAND_SNF:
            photoObj.forEach((item: any) => {
              standardObjects.push({
                _id: item._id,
                createDate: item.createDate,
                deletionDate: item.deletionDate,
                updateDate: item.updateDate,
                version: item.version,
                markedForDelete: item.markedForDelete,
                dataCenter: item.dataCenter,
                context: item.context,
                assetSource: 'snapfish',
                reportingMediaSource: 'snapfish',
                ownerAccountId: item.ownerAccountId,
                userTags: [
                  {
                    key: 'userFileName',
                    value:
                      item.userTags.find(
                        (tag: any) => tag.key === 'userFileName',
                      )?.value || '',
                  },
                  {
                    key: 'caption',
                    value:
                      item.userTags.find((tag: any) => tag.key === 'caption')
                        ?.value || '',
                  },
                  {
                    key: 'fileName',
                    value:
                      item.userTags.find((tag: any) => tag.key === 'fileName')
                        ?.value || '',
                  },
                ],
                systemTags: [
                  { key: 'journalDeveloper', value: item.journalDeveloper },
                  { key: 'journalCore', value: item.journalCore },
                  { key: 'assetStatus', value: item.assetStatus },
                ],
                status: item.status,
                currentImageInfo: {
                  currentWidth: item.currentImageInfo.currentWidth,
                  currentHeight: item.currentImageInfo.currentHeight,
                },
                exifTags: [],
                assetType: item.assetType,
                files: [
                  {
                    fileType: 'HIRES',
                    url: '',
                    width: item.currentImageInfo.currentWidth,
                    height: item.currentImageInfo.currentHeight,
                    size: item.files.find(
                      (file: any) => file.fileType === 'HIRES',
                    )?.size,
                  },
                  {
                    fileType: 'THUMBNAIL',
                    url:
                      item.files.find(
                        (file: any) => file.fileType === 'THUMBNAIL',
                      )?.url || '',
                    width: item.currentImageInfo.currentWidth,
                    height: item.currentImageInfo.currentHeight,
                    size: item.files.find(
                      (file: any) => file.fileType === 'THUMBNAIL',
                    )?.size,
                  },
                ],
                parentCollectionReferences: [
                  {
                    ownerAccountId: item.ownerAccountId,
                    collectionId: item.collectionId,
                  },
                ],
                faces: [],
                dateTaken: item.dateTaken,
                subType: item.subType,
                id: item._id,
              });
            });
            break;

          default:
            break;
        }
        break;
      case Constants.SOURCE_BULK_API:
        photoObj.forEach((item: any) => {
          const { imageID, DATA } = this.extractImageDataFromUrl(
            item.thumbnail,
          );

          const smallThumbnailUrl = `imageID=${imageID}&DATA=${DATA}`;

          const urlParams = new URLSearchParams(item.thumbnail.split('?')[1]);

          let journalCore = this.extractJournalCoreFromUrl(item.thumbnail);
          const DEFAULT_DELETED_IMAGE_WIDTH = 406;
          const DEFAULT_DELETED_IMAGE_HEIGHT = 304;
          let effect = urlParams.get('effect');
          let rotation = urlParams.get('rotation');
          let cropll: string;
          let cropur: string;

          if (item.shouldNotConsiderCrop) {
            cropll = '0,0';
            cropur = '1,1';
          } else {
            cropll = urlParams.get('cropll') ?? '';
            cropur = urlParams.get('cropur') ?? '';
          }

          let redeye = urlParams.get('redeye') ? 'Yes' : 'No';

          if (effect && effect.includes(':')) {
            effect = effect.split(':')[0];
          }

          const userTags = [
            { key: 'userFileName', value: item.title },
            { key: 'caption', value: item.title },
            { key: 'fileName', value: item.title },
            { key: 'hiresURL', value: item.url },
            {
              key: 'locationSpec',
              value: item.locationspec || item.locationSpec || '',
            },
          ];

          if (rotation === '90' || rotation === '180' || rotation === '270') {
            const cropllArr =
              typeof cropll === 'string'
                ? cropll.split(',').map((val) => parseFloat(val))
                : [];

            const cropurArr =
              typeof cropur === 'string'
                ? cropur.split(',').map((val) => parseFloat(val))
                : [];

            const validCoords =
              cropllArr.length === 2 &&
              cropurArr.length === 2 &&
              !cropllArr.some(isNaN) &&
              !cropurArr.some(isNaN);

            if (validCoords) {
              const rotated = MediaObjectMapper.unrotateShutterflyCrop(
                cropllArr,
                cropurArr,
                parseInt(rotation, 10),
              );

              cropll = `${rotated.cropll[0].toFixed(2)},${rotated.cropll[1].toFixed(2)}`;
              cropur = `${rotated.cropur[0].toFixed(2)},${rotated.cropur[1].toFixed(2)}`;
            }
          }

          if (rotation) {
            userTags.push({ key: 'rotation', value: rotation });
          }
          if (cropll) {
            userTags.push({ key: 'cropll', value: cropll });
          }
          if (cropur) {
            userTags.push({ key: 'cropur', value: cropur });
          }
          if (effect) {
            userTags.push({ key: 'effect', value: effect });
          }
          if (redeye) {
            userTags.push({ key: 'redeye', value: redeye });
          }

          let exifHeaders = item.exifHeaders ? { ...item.exifHeaders } : null;

          let cwidth: number, cheight: number;
          if (
            item.state &&
            (item.state === 'USER_DELETED' ||
              item.state === 'DELETED' ||
              item.state === 'USER_SHARE_DELETED')
          ) {
            cwidth = DEFAULT_DELETED_IMAGE_WIDTH;
            cheight = DEFAULT_DELETED_IMAGE_HEIGHT;
          } else if (rotation === '90' || rotation === '270') {
            cwidth = item.image_def.primary_height;
            cheight = item.image_def.primary_width;
          } else {
            cwidth = item.image_def.primary_width;
            cheight = item.image_def.primary_height;
          }
          if (
            !item.state ||
            (item.state !== 'USER_DELETED' &&
              item.state !== 'DELETED' &&
              item.state !== 'USER_SHARE_DELETED')
          ) {
            if (item.shouldNotConsiderCrop) {
              const isRotated = rotation === '90' || rotation === '270';
              [cwidth, cheight] = isRotated
                ? [item.image_def.primary_height, item.image_def.primary_width]
                : [item.image_def.primary_width, item.image_def.primary_height];

              // Force crop area to full image (0,0,1,1) when cropping should be ignored
              journalCore = this.replaceWithFullImageCrop(journalCore);
            } else {
              const { width, height } = this.getModifiedDimensionsFromJournal(
                journalCore,
                item.image_def.primary_width,
                item.image_def.primary_height,
                rotation || undefined,
              );

              cwidth = width;
              cheight = height;
            }
          }

          standardObjects.push({
            _id: String(item.id),
            ...(item.requestId != null &&
              item.requestId !== '' && { requestId: String(item.requestId) }),
            createDate: item.instime,
            deletionDate: null,
            updateDate: item.updtime,
            version: null,
            markedForDelete: null,
            fileName: item.fileName,
            state: item.state,
            dataCenter: 'AUS',
            context: '/sfly/shutterfly/shutterfly-us/default',
            assetSource: 'shutterfly',
            reportingMediaSource: 'shutterfly',
            ownerAccountId: item.userId,
            userTags: userTags,
            systemTags: [
              { key: 'journalDeveloper', value: '' },
              { key: 'journalCore', value: journalCore },
              { key: 'assetStatus', value: 'AVAILABLE' },
            ],
            status: 'AVAILABLE',
            currentImageInfo: {
              currentWidth: cwidth,
              currentHeight: cheight,
            },
            exifTags: [],
            exifHeaders: exifHeaders,
            assetType: item.assetType,
            files: [
              {
                fileType: 'HIRES',
                url: '',
                width: [
                  'USER_DELETED',
                  'DELETED',
                  'USER_SHARE_DELETED',
                ].includes(item?.state)
                  ? DEFAULT_DELETED_IMAGE_WIDTH
                  : item.image_def.primary_width,
                height: [
                  'USER_DELETED',
                  'DELETED',
                  'USER_SHARE_DELETED',
                ].includes(item?.state)
                  ? DEFAULT_DELETED_IMAGE_HEIGHT
                  : item.image_def.primary_height,
                size: undefined,
              },
              {
                fileType: 'THUMBNAIL',
                url: smallThumbnailUrl,
                width: [
                  'USER_DELETED',
                  'DELETED',
                  'USER_SHARE_DELETED',
                ].includes(item?.state)
                  ? DEFAULT_DELETED_IMAGE_WIDTH
                  : item.image_def.primary_width,
                height: [
                  'USER_DELETED',
                  'DELETED',
                  'USER_SHARE_DELETED',
                ].includes(item?.state)
                  ? DEFAULT_DELETED_IMAGE_HEIGHT
                  : item.image_def.primary_height,
                size: undefined,
              },
            ],
            parentCollectionReferences: [
              { ownerAccountId: item.ownerAccountId, collectionId: undefined },
            ],
            faces: [],
            dateTaken: item.image_def.capture_time,
            subType: item.subType,
            id: String(item.id),
          });
        });
        break;
    }

    return standardObjects;
  }

  static getUpdatedProxyUrl(
    host: string,
    assetRef: string,
    journals: string,
    height: number,
    brand: string,
  ): string {
    let domainURL: string;

    // Ensure protocol in host
    const url = host.startsWith('http') ? host : 'https://' + host;

    if (brand === 'sfly') {
      const size = this.calculateSize(height);
      const journalParams: string[] = journals
        .split('/')
        .filter(
          (param) =>
            param.startsWith('ca=') ||
            param.startsWith('rf=') ||
            param.startsWith('ef=') ||
            param.startsWith('redeye=') ||
            param.startsWith('en='),
        );
      const mappedCropping = this.getJournalMapping(journalParams, brand);
      const mappedCroppingString = mappedCropping
        ? this.formatObjectToUrl(mappedCropping)
        : '';
      domainURL = `${url}/procsimple`;

      const urlObj = new URL(domainURL);
      const existingParams = urlObj.searchParams;

      if (mappedCroppingString) {
        const mappedCroppingParams = new URLSearchParams(mappedCroppingString);
        mappedCroppingParams.forEach((value, key) => {
          existingParams.set(key, value);
        });
      }

      const queryString = Array.from(existingParams.entries())
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      domainURL = `${urlObj.origin}${urlObj.pathname}?${queryString}${urlObj.hash}&${assetRef}&rendersize=${size}`;
      domainURL = domainURL.replace('?&', '?');
    } else if (brand === 'snf') {
      domainURL = `${url}/assetrenderer/v2/thumbnail/${assetRef}?height=${height}`;
    } else {
      throw new Error('Invalid brand.');
    }

    return domainURL;
  }

  static getBulkAPIUrl(
    host: string,
    userId: string,
    brand: string,
    reqPayload: string[],
  ): BulkApiObject {
    let bulkApiObject: BulkApiObject = { apiUrl: '', payload: [] };
    let url;

    if (host.includes('http://') || host.includes('https://')) {
      url = host;
    } else {
      url = 'https://' + host;
    }

    let brandSpecificURL: string = '';
    let imageReference = reqPayload;

    try {
      if (typeof host !== 'string' || host.trim() === '') {
        throw new Error('Invalid Host');
      }

      if (typeof brand !== 'string' || brand.trim() === '') {
        throw new Error('Invalid Brand');
      }

      if (!Array.isArray(reqPayload) || reqPayload.length === 0) {
        throw new Error('reqPayload must be a non-empty array');
      }

      if (brand === 'sfly') {
        if (typeof userId !== 'string' || userId.trim() === '') {
          throw new Error('Invalid userId');
        }

        brandSpecificURL = `${url}/services/user-media/${userId}/images/bulk`;
        bulkApiObject['payload'] = imageReference;
      } else if (brand === 'snf') {
        brandSpecificURL = `${url}/pict/v2/asset/list`;
        bulkApiObject['payload'] = { AssetReferences: imageReference };
      }
    } catch (error: any) {
      console.error('Error occurred while processing:', error.message);
    }

    bulkApiObject['apiUrl'] = brandSpecificURL;

    return bulkApiObject;
  }

  static getJournalMapping(
    journalList: string[],
    brand: string,
  ): { [key: string]: string } {
    const mappedObject: { [key: string]: string } = {};
    const journalOps = new JournalOperations();

    const cropping = journalOps.getCropping(brand, journalList);
    const rotations = journalOps.getRotations(brand, journalList);
    const filters = journalOps.getFilters(brand, journalList);

    Object.keys(cropping).forEach((key) => (mappedObject[key] = cropping[key]));
    Object.keys(rotations).forEach(
      (key) => (mappedObject[key] = rotations[key]),
    );
    Object.keys(filters).forEach((key) => (mappedObject[key] = filters[key]));

    return mappedObject;
  }

  static calculateSize(height: number): string {
    // Check if height is within the valid range
    if (height < 96) {
      return 'fit96x96';
    } else if (height >= 3600) {
      return 'fit3600x3600';
    }

    const heightValues: number[] = [
      96, 200, 350, 480, 540, 700, 800, 1000, 1600, 2000, 2600, 3000, 3600,
    ];

    // Smallest size that is the same as or larger than the requested height
    const size: number = heightValues.find((value) => value >= height) ?? 96;

    return `fit${size}x${size}`;
  }

  static extractImageDataFromUrl(url: string): {
    imageID?: string;
    DATA?: string;
  } {
    if (!url || typeof url !== 'string') {
      return {};
    }

    const regex = /[?&](?:imageID|DATA)=([^&]+)/g;
    const match = url.match(regex);
    let imageData: { imageID?: string; DATA?: string } = {};

    if (match) {
      match.forEach((param) => {
        const [key, value] = param.split('=');
        // Use type assertion to handle the error
        (imageData as any)[key.substring(1)] = value;
      });
    }

    return imageData;
  }

  static formatObjectToUrl(obj: { [key: string]: string }): string {
    return Object.entries(obj)
      .map(([key, value]) => `${value}`)
      .join('&');
  }

  static extractJournalCoreFromUrl(url: string): string {
    const regex = /[?&](rotation|cropll|cropur|effect|redeye)=([^&]+)/g;
    const match = url.matchAll(regex);
    const extractedValues: { [key: string]: string } = {};

    for (const param of match) {
      const [_, key, value] = param;
      if (key === 'cropll' || key === 'cropur') {
        if (!extractedValues[key]) {
          extractedValues[key] = '';
        }
        extractedValues[key] += `${value},`;
      } else {
        extractedValues[key] = value;
      }
    }

    const formattedValues: string[] = [];
    for (const key in extractedValues) {
      if (Object.prototype.hasOwnProperty.call(extractedValues, key)) {
        if (key === 'cropll') {
          const cropllValue = extractedValues['cropll']
            ? extractedValues['cropll'].slice(0, -1)
            : '';
          const cropurValue = extractedValues['cropur']
            ? extractedValues['cropur'].slice(0, -1)
            : '';
          if (cropurValue) {
            formattedValues.push(`${key}=${cropllValue}&cropur=${cropurValue}`);
          } else {
            formattedValues.push(`${key}=${cropllValue}`);
          }
        } else {
          formattedValues.push(`${key}=${extractedValues[key]}`);
        }
      }
    }

    const mappedCropping = this.getJournalMapping(formattedValues, 'snf');
    let formattedString = '';

    if (Object.keys(mappedCropping).length > 0) {
      formattedString = '/';
      Object.keys(mappedCropping).forEach((key) => {
        const value = mappedCropping[key];
        formattedString += value + '/';
      });
    }

    return formattedString;
  }

  static getStandardAlbumsResponse(albumsObject: any): any {
    const response = {
      entities: [] as AlbumsEntity[],
      entityMap: {},
      errorId: null,
      errors: [],
      resultCount: albumsObject.length,
      results: [],
      totalEntitiesCount: 0,
    };

    albumsObject.forEach((item: any) => {
      const story = item.story;

      if (
        story.path !== 'mystory' ||
        (story.path === 'mystory' && story.moment_count > 0)
      ) {
        const shared = story.is_shared ? 'Yes' : 'No';

        const entity: AlbumsEntity = {
          '@class': 'Collection',
          accessLevel: 'OWNER',
          assetIdList: [],
          collectionDate: null,
          collectionType: null,
          createDate: story.created,
          dataCenter: 'AUS',
          firstAssetId: {
            assetId: story.cover_moment_uid,
            assetType: 'PICTURE',
            ownerId: story.cover_owner_uid,
            thumbnailEncryption: story.cover_moment_encrypted_id,
          },
          id: story.uid,
          shared: shared,
          status: null,
          markedForDelete: false,
          ownerAccountId: story.cover_owner_uid,
          systemTags: [
            {
              key: 'sortCode',
              value: 'DDS',
            },
          ],
          updateDate: story.updated,
          userTags: [
            {
              key: 'caption',
              value: story.name,
            },
          ],
          version: 3,
        };

        response.entities.push(entity);
      }
    });

    return response;
  }

  static albumMomentsResponse(momentsObject: any): any {
    const uids = momentsObject.map((moment: any) => moment.uid);
    return uids;
  }

  static standardPhotosResponse(albumMomentsObject: any): any {
    const entities = albumMomentsObject.map((photo: any) => {
      const hiresUrl =
        'https://ipcloud.dev.shutterfly.com/servlet/procsimple/raw?' +
        photo.url.split('?')[1];
      const urlParams = new URLSearchParams(photo.thumbnail.split('?')[1]);
      let effect = urlParams.get('effect');
      const redeye = urlParams.get('redeye') ? 'Yes' : 'No';

      if (effect && effect.includes(':')) {
        effect = effect.split(':')[0];
      }

      const userTags = [
        {
          key: 'fileName',
          value: photo.title,
        },
        {
          key: 'caption',
          value: photo.title,
        },
      ];

      if (effect) {
        userTags.push({
          key: 'effect',
          value: effect,
        });
      }

      userTags.push({
        key: 'redeye',
        value: redeye,
      });

      return {
        '@class': 'Asset',
        assetType: 'PICTURE',
        createDate: new Date(photo.instime).getTime(),
        dataCenter: 'AUS',
        dateTaken: new Date(photo.image_def.capture_time).getTime(),
        exifTags: [],
        faces: [],
        files: [
          {
            fileType: 'HIRES',
            height: photo.image_def.height,
            size: 0,
            url: hiresUrl,
            width: photo.image_def.width,
          },
          {
            fileType: 'THUMBNAIL',
            height: photo.image_def.height,
            size: 0,
            url: photo.thumbnail,
            width: photo.image_def.width,
          },
        ],
        id: photo.id,
        markedForDelete: false,
        ownerAccountId: photo.userId,
        parentCollectionReferences: [],
        currentImageInfo: {
          currentWidth:
            photo.image_def.editedWidth !== undefined
              ? photo.image_def.editedWidth
              : photo.image_def.primary_width,
          currentHeight:
            photo.image_def.editedHeight !== undefined
              ? photo.image_def.editedHeight
              : photo.image_def.primary_height,
        },
        systemTags: [
          {
            key: 'uploadTypeCode',
            value: 'TST',
          },
          {
            key: 'displayOrder',
            value: new Date(photo.instime).getTime(),
          },
        ],
        updateDate: new Date(photo.updtime).getTime(),
        userTags: userTags,
        version: 2,
      };
    });

    return {
      entities,
      entityMap: {},
      errorId: null,
      errors: [],
      resultCount: entities.length,
      results: [],
      totalEntitiesCount: albumMomentsObject.length,
    };
  }

  static getUpdatedHiresUrl(
    host: string,
    assetRef: string,
    journals: string,
    height: number,
    brand: string,
  ): string {
    let domainURL: string;

    // Ensure protocol in host
    const url = host.startsWith('http') ? host : 'https://' + host;

    if (brand === 'sfly') {
      const size = this.calculateSize(height);
      const journalParams: string[] = journals
        .split('/')
        .filter(
          (param) =>
            param.startsWith('ca=') ||
            param.startsWith('rf=') ||
            param.startsWith('ef=') ||
            param.startsWith('redeye=') ||
            param.startsWith('en='),
        );
      const mappedCropping = this.getJournalMapping(journalParams, brand);
      const mappedCroppingString = mappedCropping
        ? this.formatObjectToUrl(mappedCropping)
        : '';
      domainURL = `${url}/servlet/procsimple/raw`;

      const urlObj = new URL(domainURL);
      const existingParams = urlObj.searchParams;

      if (mappedCroppingString) {
        const mappedCroppingParams = new URLSearchParams(mappedCroppingString);
        mappedCroppingParams.forEach((value, key) => {
          existingParams.set(key, value);
        });
      }

      const queryString = Array.from(existingParams.entries())
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      domainURL = `${urlObj.origin}${urlObj.pathname}?${queryString}${urlObj.hash}&${assetRef}&rendersize=${size}`;
      domainURL = domainURL.replace('?&', '?');
    } else if (brand === 'snf') {
      domainURL = `${url}/assetrenderer/v2/thumbnail/${assetRef}?height=${height}`;
    } else {
      throw new Error('Invalid brand.');
    }

    return domainURL;
  }

  static getTagValueFromTags(
    userTags: { key: string; value: string }[],
    key: string,
  ): string | undefined {
    const tag = userTags.find((tag) => tag.key === key);
    return tag ? tag.value : undefined;
  }

  static getKrakenPhotoObject(
    photoObj: any[],
    imgHost: string,
  ): StandardKrakenObject[] {
    return photoObj.map((item: any): StandardKrakenObject => {
      const thumbnailFile = item.files.find(
        (file: any) => file.fileType === 'THUMBNAIL',
      );
      const urlParams = new URLSearchParams(thumbnailFile.url);
      const imageID = urlParams.get('imageID') || '';
      const DATA = urlParams.get('DATA') || '';
      const smallThumbnailUrl = `${imgHost}/procsimple?imageID=${imageID}&DATA=${DATA}&rendersize=fit350x350`;
      const largeThumbnailUrl = `${imgHost}/procsimple?imageID=${imageID}&DATA=${DATA}&rendersize=fit800x800`;

      const userFileName =
        this.getTagValueFromTags(item.userTags, 'userFileName') || '';
      const fileName =
        this.getTagValueFromTags(item.userTags, 'fileName') || '';
      const locationSpec =
        this.getTagValueFromTags(item.userTags, 'locationSpec') || '';
      const journals =
        this.getTagValueFromTags(item.systemTags, 'journalCore') || '';
      const journalParams: string[] = journals
        .split('/')
        .filter(
          (param) =>
            param.startsWith('ca=') ||
            param.startsWith('rf=') ||
            param.startsWith('ef=') ||
            param.startsWith('redeye=') ||
            param.startsWith('en='),
        );

      const mappedJournals = this.getJournalMapping(
        journalParams,
        Constants.BRAND_SFLY,
      );

      const journalQueryString = Object.entries(mappedJournals)
        .map(([key, value]) => `${value}`)
        .join('&');

      const procSimpleUrl =
        `${imgHost}/procsimple?imageID=${imageID}&DATA=${DATA}` +
        journalQueryString;

      const sourceCropParams = journalParams.find((param) =>
        param.startsWith('ca='),
      );
      const sourceEffectParams = journalParams.find((param) =>
        param.startsWith('ef='),
      );
      const sourceRotationParams = journalParams.find((param) =>
        param.startsWith('rf='),
      );
      const enhanceParams = journalParams.find((param) =>
        param.startsWith('en='),
      );

      let sourceCrop;
      let cropll;
      let cropur;

      /*if (sourceCropParams) {
        sourceCrop = mappedJournals[sourceCropParams].split('&');

        cropll = sourceCrop[0].replace('cropll=', '').split(',');
        cropur = sourceCrop[1].replace('cropur=', '').split(',');

        sourceCrop = {
          llx: parseFloat(cropll[0]),
          lly: parseFloat(cropll[1]),
          urx: parseFloat(cropur[0]),
          ury: parseFloat(cropur[1]),
        };
      } else {
        sourceCrop = {
          llx: '',
          lly: '',
          urx: '',
          ury: '',
        };
      }*/

      let sourceEffect;
      if (sourceEffectParams) {
        sourceEffect = mappedJournals[sourceEffectParams].split('=');
        sourceEffect = sourceEffect[1];
      } else {
        sourceEffect = '';
      }

      let sourceRotation;
      if (sourceRotationParams) {
        sourceRotation = mappedJournals[sourceRotationParams].split('=');
        sourceRotation = sourceRotation[1];
      } else {
        sourceRotation = '';
      }

      let sourceEnhance;
      if (enhanceParams) {
        sourceEnhance = mappedJournals[enhanceParams].split('=');
        sourceEnhance = sourceEnhance[1];
      } else {
        sourceEnhance = '';
      }

      if (sourceCropParams) {
        sourceCrop = mappedJournals[sourceCropParams].split('&');

        cropll = sourceCrop[0].replace('cropll=', '').split(',');
        cropur = sourceCrop[1].replace('cropur=', '').split(',');

        const rotationAngle = parseInt(sourceRotation, 10);

        let cropll_kranken = [parseFloat(cropll[0]), parseFloat(cropll[1])];
        let cropurkranken = [parseFloat(cropur[0]), parseFloat(cropur[1])];

        if (!isNaN(rotationAngle)) {
          const rotatedCrop = MediaObjectMapper.rotateShutterflyCrop(
            cropll_kranken,
            cropurkranken,
            rotationAngle,
          );

          sourceCrop = {
            llx: rotatedCrop.cropll[0].toFixed(2),
            lly: rotatedCrop.cropll[1].toFixed(2),
            urx: rotatedCrop.cropur[0].toFixed(2),
            ury: rotatedCrop.cropur[1].toFixed(2),
          };
        } else {
          console.warn('Invalid rotation angle:', sourceRotation);
          // fallback if needed
          sourceCrop = {
            llx: cropll[0],
            lly: cropll[1],
            urx: cropur[0],
            ury: cropur[1],
          };
        }
      } else {
        sourceCrop = {
          llx: '',
          lly: '',
          urx: '',
          ury: '',
        };
      }
      /*sourceCrop = {
          llx: parseFloat(cropll[0]),
          lly: parseFloat(cropll[1]),
          urx: parseFloat(cropur[0]),
          ury: parseFloat(cropur[1]),
        };
      } else {
        sourceCrop = {
          llx: '',
          lly: '',
          urx: '',
          ury: '',
        };
      }*/

      return {
        anon: false,
        batchNumber: '',
        height: item.currentImageInfo.currentHeight || '',
        hidden: false,
        largeThumbnailUrl: largeThumbnailUrl,
        largeThumbnailUrlEffects: largeThumbnailUrl,
        oldHeight: item.currentImageInfo.currentHeight || '',
        oldWidth: item.currentImageInfo.currentWidth || '',
        preprocessing: true,
        qrUpload: false,
        smallThumbnailUrl: smallThumbnailUrl,
        smallThumbnailUrlEffects: smallThumbnailUrl,
        width: item.currentImageInfo.currentWidth || '',
        xid: '',
        captureTime: '',
        data: {
          locationSpec: locationSpec || '',
          url: '',
          mimeType: '',
          width: item.currentImageInfo.currentWidth || '',
          height: item.currentImageInfo.currentHeight || '',
          mediaId: item.id || '',
          renderEncryptedId: '',
          capturedDate: item.dateTaken || '',
          uploadDate: item.createDate,
          viewEditStateData: '',
          encodedImagePath: imageID || '',
          imagePath: '',
          favorite: false,
          rotation: 'D_0',
          image_def: {
            primary_width: `${(item.files as FileObject[]).find((f) => f.fileType === 'HIRES')?.width ?? 0}`,
            primary_height: `${(item.files as FileObject[]).find((f) => f.fileType === 'HIRES')?.height ?? 0}`,
          },
        },

        filename: userFileName,
        imageData: item.imageData || '',
        imageId: `${imageID}` || '',
        isDupe: item.isDupe || '',
        locationSpec: locationSpec || '',
        origin: item.origin || '',
        sourceCrop: sourceCrop,
        sourceEffect: sourceEffect,
        sourceRotation: sourceRotation,
        sflyId: item.id,
        title: userFileName,
        uploadTime: item.createDate,
        procSimpleUrl: procSimpleUrl,
        ...(sourceEnhance ? { enhance: sourceEnhance } : {}),
      };
    });
  }

  static getImageEditData(photoObj: any): {
    rotation: number;
    Crop?: string;
    Effect?: string;
  } {
    const userData = photoObj.content?.userData || {};

    let journalCoreArray: string[] = [];
    if (typeof userData.journalCore === 'string') {
      journalCoreArray = userData.journalCore
        .split('/')
        .filter((part: string) => part !== '');
    }

    const mappedCropping = this.getJournalMapping(journalCoreArray, 'sfly');

    let rotation = userData.rot ?? 0;
    let Crop: string | undefined;
    let Effect: string | undefined;

    for (const key in mappedCropping) {
      if (key.startsWith('ca=')) {
        Crop = mappedCropping[key];
      } else if (key.startsWith('rf=')) {
        const rotationMatch = mappedCropping[key].match(/rotation=(\d+)/);
        if (rotationMatch) {
          rotation = parseInt(rotationMatch[1], 10);
        }
      } else if (key.startsWith('ef=')) {
        const effectMatch = mappedCropping[key].match(/effect=(.*)/);
        if (effectMatch) {
          Effect = effectMatch[1].split(':')[0];
        }
      }
    }

    // Return the extracted data
    return {
      rotation,
      Crop,
      Effect,
    };
  }

  static getMmbPhotosUrl(
    host: string,
    userId: string,
    version: string,
    brand: string,
    reqPayload: { [key: string]: string[] }, // Update reqPayload to accept an object with string[] values
  ): MmbPhotosObject {
    let MmbPhotosObject: MmbPhotosObject = { apiUrl: '', payload: {} }; // Adjust initial payload type
    let url;

    if (host.includes('http://') || host.includes('https://')) {
      url = host;
    } else {
      url = 'https://' + host;
    }
    let brandSpecificURL: string = '';

    try {
      if (typeof host !== 'string' || host.trim() === '') {
        throw new Error('Invalid Host');
      }

      if (typeof brand !== 'string' || brand.trim() === '') {
        throw new Error('Invalid Brand');
      }

      if (
        typeof reqPayload !== 'object' ||
        Object.keys(reqPayload).length === 0
      ) {
        throw new Error('reqPayload must be a non-empty object');
      }

      if (brand === 'sfly') {
        if (typeof userId !== 'string' || userId.trim() === '') {
          throw new Error('Invalid userId');
        }

        brandSpecificURL = `${url}/services/user-media/${version}/${userId}/images/bulk`;
        MmbPhotosObject['payload'] = reqPayload; // Assign the object payload directly
      }
    } catch (error: any) {
      console.error('Error occurred while processing:', error.message);
    }

    MmbPhotosObject['apiUrl'] = brandSpecificURL;

    return MmbPhotosObject;
  }

  static getBulkImageUrl(
    host: string,
    userId: string,
    version: string,
    reqPayload: string[],
  ): BulkImageObject {
    let BulkImageObject: BulkImageObject = { apiUrl: '', payload: [] };
    let url;

    if (host.includes('http://') || host.includes('https://')) {
      url = host;
    } else {
      url = 'https://' + host;
    }

    let brandSpecificURL: string = '';
    let imageReference = reqPayload;

    try {
      if (typeof host !== 'string' || host.trim() === '') {
        throw new Error('Invalid Host');
      }

      if (typeof version !== 'string' || version.trim() === '') {
        throw new Error('Invalid Version');
      }

      if (!Array.isArray(reqPayload) || reqPayload.length === 0) {
        throw new Error('reqPayload must be a non-empty array');
      }

      if (typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('Invalid userId');
      }

      brandSpecificURL = `${url}/services/user-media/${version}/${userId}/imageId/bulk`;
      BulkImageObject['payload'] = imageReference;
    } catch (error: any) {
      console.error('Error occurred while processing:', error.message);
    }

    BulkImageObject['apiUrl'] = brandSpecificURL;

    return BulkImageObject;
  }

  static getBulkImageRefUrl(
    host: string,
    userId: string,
    version: string,
    reqPayload: Record<string, string>,
  ): BulkImageRefObject {
    let BulkImageRefObject: BulkImageRefObject = {
      apiUrl: '',
      payload: {},
    };

    let url =
      host.startsWith('http://') || host.startsWith('https://')
        ? host
        : `https://${host}`;

    try {
      if (typeof host !== 'string' || host.trim() === '') {
        throw new Error('Invalid Host');
      }
      if (typeof version !== 'string' || version.trim() === '') {
        throw new Error('Invalid Version');
      }
      if (
        !reqPayload ||
        typeof reqPayload !== 'object' ||
        Object.keys(reqPayload).length === 0
      ) {
        throw new Error('reqPayload must be a non-empty object');
      }
      if (typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('Invalid userId');
      }

      BulkImageRefObject.apiUrl = `${url}/services/user-media/${version}/${userId}/imageRef/bulk`;
      BulkImageRefObject.payload = reqPayload;
    } catch (error: any) {
      console.error('Error occurred while processing:', error.message);
    }

    return BulkImageRefObject;
  }

  static rotateShutterflyCrop(
    cropll: number[],
    cropur: number[],
    angle: number,
  ) {
    const x1 = cropll[0],
      y1 = cropll[1];
    const x2 = cropur[0],
      y2 = cropur[1];

    let points = [
      [x1, y1],
      [x2, y2],
    ];

    // Apply rotation transformation
    let rotated = points.map(([x, y]) => {
      if (angle === 90) {
        return [y, 1 - x]; // 90° clockwise
      } else if (angle === 180) {
        return [1 - x, 1 - y];
      } else if (angle === 270) {
        return [1 - y, x]; // 270° clockwise (or 90° counter-clockwise)
      } else {
        throw new Error('Only 90 or 270 degrees supported.');
      }
    });

    const xs = rotated.map((p) => p[0]);
    const ys = rotated.map((p) => p[1]);

    const llx = Math.min(...xs);
    const lly = Math.min(...ys);
    const urx = Math.max(...xs);
    const ury = Math.max(...ys);

    return {
      cropll: [llx, lly],
      cropur: [urx, ury],
    };
  }

  static unrotateShutterflyCrop(
    cropll: number[],
    cropur: number[],
    angle: number,
  ) {
    const x1 = cropll[0],
      y1 = cropll[1];
    const x2 = cropur[0],
      y2 = cropur[1];

    let points = [
      [x1, y1],
      [x2, y2],
    ];

    // Apply reverse transformation based on angle
    let transformed = points.map(([x, y]) => {
      if (angle === 90) {
        return [1 - y, x]; // Reverse of 90° clockwise = 90° counter-clockwise
      } else if (angle === 180) {
        return [1 - x, 1 - y];
      } else if (angle === 270) {
        return [y, 1 - x]; // Reverse of 270° clockwise = 90° clockwise
      } else {
        throw new Error('Only 90 or 270 degrees supported.');
      }
    });

    const xs = transformed.map((p) => p[0]);
    const ys = transformed.map((p) => p[1]);

    const llx = Math.min(...xs);
    const lly = Math.min(...ys);
    const urx = Math.max(...xs);
    const ury = Math.max(...ys);

    return {
      cropll: [llx, lly],
      cropur: [urx, ury],
    };
  }

  static getJournalCoreAPC(url: string): { [key: string]: string } {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const journalList: string[] = [];

    const keys = ['rotation', 'cropll', 'cropur', 'effect', 'redeye'];
    keys.forEach((key) => {
      const value = urlParams.get(key);
      if (value !== null) {
        if (key === 'cropll') {
          const cropur = urlParams.get('cropur');
          if (cropur) {
            journalList.push(`cropll=${value}&cropur=${cropur}`);
          } else {
            journalList.push(`cropll=${value}`);
          }
        } else if (key !== 'cropur') {
          journalList.push(`${key}=${value}`);
        }
      }
    });

    return this.getJournalMapping(journalList, 'snf');
  }

  static getModifiedDimensionsFromJournal(
    journalCore: string,
    originalWidth: number,
    originalHeight: number,
    rotation?: string | number,
  ): { width: number; height: number } {
    let modifiedWidth = originalWidth;
    let modifiedHeight = originalHeight;
    const caEntry = journalCore
      .split('/')
      .find((entry) => entry.startsWith('ca='));

    if (!caEntry) {
      return { width: modifiedWidth, height: modifiedHeight };
    }

    const cropStr = caEntry.replace('ca=', '');
    const coords = cropStr.split(',').map((v) => parseFloat(v));

    if (coords.length < 4 || coords.some(isNaN)) {
      return { width: modifiedWidth, height: modifiedHeight };
    }

    const [x1, y1, x2, y2] = coords;
    const angle =
      typeof rotation === 'string' ? parseInt(rotation, 10) : rotation;

    if (angle === 90 || angle === 270) {
      // rotated
      modifiedWidth = Math.round(originalHeight * (x2 - x1));
      modifiedHeight = Math.round(originalWidth * (y2 - y1));
    } else {
      // not rotated
      modifiedWidth = Math.round(originalWidth * (y2 - y1));
      modifiedHeight = Math.round(originalHeight * (x2 - x1));
    }

    return { width: modifiedWidth, height: modifiedHeight };
  }

  static replaceWithFullImageCrop(journal: string): string {
    const CROP_AREA_REGEX = /\/ca=[^/]+\//;
    const FULL_IMAGE_CROP = '/ca=0,0,1,1/';
    return journal.replace(CROP_AREA_REGEX, FULL_IMAGE_CROP);
  }
}
